import apiClient from './apiClient';
import type { AuthUser } from '../stores/authStore';

const API = import.meta.env.VITE_API_BASE_URL || '';

export interface LoginResult {
  token: string;
  user: AuthUser;
}

// Current user from the bearer token. Used to re-hydrate the user object after a
// reload (token is persisted, user is not).
export async function me(): Promise<AuthUser> {
  const { data } = await apiClient.get<AuthUser>('/api/v1/auth/me');
  return data;
}

// Exchange the MSAL tokens for an app JWT + user. The backend VERIFIES the
// id_token (to prevent token-substitution); the access_token is sent alongside
// for any Graph calls it may need.
export async function loginWithMicrosoft(
  idToken: string,
  accessToken?: string
): Promise<LoginResult> {
  const { data } = await apiClient.post<LoginResult>('/api/v1/auth/microsoft', {
    id_token: idToken,
    access_token: accessToken,
  });
  return data;
}

// Email + password sign-in. 401 invalid_credentials on bad creds.
export async function login(email: string, password: string): Promise<LoginResult> {
  const { data } = await apiClient.post<LoginResult>('/api/v1/auth/login', {
    email,
    password,
  });
  return data;
}

// Email + password registration. 400 on validation error, 409 email_in_use.
export async function register(
  email: string,
  name: string,
  password: string
): Promise<LoginResult> {
  const { data } = await apiClient.post<LoginResult>('/api/v1/auth/register', {
    email,
    name,
    password,
  });
  return data;
}

// Google uses a server-side redirect flow: the backend handles the OAuth dance
// and redirects back to /auth/google/callback?code=<one-time-code>. The button
// just sends the browser to the backend's start endpoint.
export function googleLoginUrl(): string {
  return `${API}/api/v1/auth/google`;
}

// Exchange a short-lived one-time code (from the Google callback redirect) for an
// app JWT + user. Keeps the JWT out of the URL and browser history.
export async function exchangeCode(code: string): Promise<LoginResult> {
  const { data } = await apiClient.post<LoginResult>('/api/v1/auth/exchange', { code });
  return data;
}
